/**
 * Updates the recently viewed products in localStorage.
 *
 * ⚠️ Fichier NATIF Horizon avec UNE divergence Crave à conserver lors d'une
 * mise à jour du thème (2026-09-20, arbitrage wording B4-c.13, consentement
 * « Personalization ») : la liste `viewedProducts` est l'une des trois
 * mémoires que la politique de confidentialité soumet au choix de la
 * visiteuse (« the site remembers neither your nail size, nor the products
 * viewed, nor the newsletter window »). Quand la catégorie est REFUSÉE dans la
 * bannière cookies, `addProduct` n'écrit plus rien, et la liste déjà stockée
 * est EFFACÉE au refus (y compris un refus antérieur relu au chargement de
 * l'API) — par le HELPER lui-même (liste `PURGE` de crave-consent.js, chargé
 * sur toutes les pages, là où ce module n'est évalué que sur les fiches et la
 * recherche), et aussi ici, de façon idempotente (`clearProducts` en bas de
 * fichier). Source de vérité = `window.craveConsent` (assets/crave-consent.js,
 * chargé en `defer` par layout/theme.liquid AVANT `snippets/scripts.liquid`,
 * qui contient le module inline `RecentlyViewed.addProduct()` des fiches
 * produit). Sans helper, ou tant qu'aucun choix n'est fait, comportement
 * natif. `getProducts` et `clearProducts` sont inchangés : la lecture d'une
 * clé absente rend simplement [] (le bloc « Recently viewed » de la recherche
 * ne se rend pas).
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in session storage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 4;

  /**
   * Crave : faux SEULEMENT sur refus explicite de « Personalization ».
   * @returns {boolean}
   */
  static #personalizationAllowed() {
    const consent = /** @type {any} */ (window).craveConsent;
    return !consent || consent.personalizationAllowed() !== false;
  }

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   */
  static addProduct(productId) {
    /* Crave (chantier securite 2026-09-22) : ATTENDRE la reponse de l'API avant
       d'ecrire. `crave-consent.js` est charge en `defer`, ce module inline
       s'executait donc AVANT elle : la cle etait ecrite puis effacee apres coup,
       y compris pour une visiteuse ayant deja refuse. `whenDecided()` est
       toujours resolue (filet a 3 s cote helper), donc rien ne reste suspendu. */
    const consent = /** @type {any} */ (window).craveConsent;
    if (consent && typeof consent.whenDecided === 'function') {
      consent.whenDecided().then((/** @type {boolean} */ allowed) => {
        if (allowed) this.#write(productId);
      });
      return;
    }
    this.#write(productId);
  }

  /**
   * Crave : l'ecriture elle-meme, appelee une fois la reponse connue.
   * @param {string} productId
   */
  static #write(productId) {
    if (!this.#personalizationAllowed()) return;

    let viewedProducts = this.getProducts();

    viewedProducts = viewedProducts.filter((/** @type {string} */ id) => id !== productId);
    viewedProducts.unshift(productId);
    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
  }

  static clearProducts() {
    localStorage.removeItem(this.#STORAGE_KEY);
  }

  /**
   * Retrieves the list of recently viewed products from session storage.
   * @returns {string[]} The list of viewed products.
   */
  static getProducts() {
    return JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
  }
}

/* Crave (voir l'en-tête) : au refus de la personnalisation, la liste quitte
   l'appareil (le helper l'efface aussi, après cet écouteur : `PURGE` de
   crave-consent.js). Enregistré une fois à l'évaluation du module (un module
   ES ne s'évalue qu'une fois par page, quel que soit le nombre d'imports). */
{
  const consent = /** @type {any} */ (window).craveConsent;
  if (consent && typeof consent.onChange === 'function') {
    consent.onChange((/** @type {boolean} */ allowed) => {
      if (allowed) return;
      try {
        RecentlyViewed.clearProducts();
      } catch (error) {
        /* stockage indisponible : rien à effacer */
      }
    });
  }
}
